import { type TabState, TabType, getStore as getTabStore } from 'owa-tab-store';
import type {
    SecondaryReadingPaneTabData,
    SecondaryReadingPaneTabViewState,
    MailComposeTabViewState,
} from 'owa-tab-store';
import type { ComposeViewState } from 'owa-mail-compose-store';
import { composeStore } from 'owa-mail-compose-store';
import getProjection from 'owa-popout-v2/lib/utils/getProjection';
import { getComposeHostItemIndex } from 'owa-addins-core';
import {
    lazyInitializeAddinsForItem,
    lazyDeinitializeAddinsForItem,
} from 'owa-addins-boot/lib/lazyFunctions';
import { lazyOnSendAddinItemSwitch } from 'owa-addins-view/lib/lazyFunctions';
import isPopout from 'owa-popout-v2/lib/utils/isPopout';
import { DialogResponse } from 'owa-confirm-dialog/lib/actions/DialogResponse';
import setIsOnSendEventTriggered from 'owa-addins-editor-plugin/lib/actions/setIsOnSendEventTriggered';
import { lazyCreateMailComposeAdapter } from 'owa-mail-addins';
import {
    lazyTrySaveAndCloseCompose,
    lazyTrySendMessageFromOnSendItemSwitchDialog,
    lazyPopoutComposeAndWaitForProjection,
    lazyMoveComposeToTab,
} from 'owa-mail-compose-actions';
import activateTab from 'owa-tab-store/lib/actions/activateTab';
import { trace } from 'owa-trace';
import closeTab from 'owa-tab-store/lib/actions/closeTab';

// Define a global variable to store the last tabComposeId
let lastTabComposeId: string | null = null;

export default async function handleComposeTab(
    activeComposeTab: MailComposeTabViewState,
    primaryTabComposeId?: string
) {
    const composeId = activeComposeTab.data ?? primaryTabComposeId;
    const composeViewState =
        composeStore.viewStates.get(composeId) ||
        ((composeStore.primaryComposeId &&
            composeStore.viewStates.get(composeStore.primaryComposeId)) as ComposeViewState);

    // Check if the active compose tab is hidden, is of type MailCompose, and if the current compose ID matches the last tab compose ID
    if (
        activeComposeTab.state === 0 &&
        activeComposeTab.type === TabType.MailCompose &&
        composeId === lastTabComposeId
    ) {
        return; // Return early if they are the same
    }
    // Update the global variable with the new tabComposeId
    lastTabComposeId = composeId ?? null;

    if (
        composeViewState?.isComposeRendered &&
        composeViewState?.addin.isOnSendEventTriggered &&
        activeComposeTab.state !== 3
    ) {
        let targetWindow = getProjection(activeComposeTab?.id)?.window || window;
        const hostItemIndex = getComposeHostItemIndex(composeViewState?.composeId);

        // In case of inline compose, store the value of isInlineCompose before we move the compose to a tab
        const isInlineCompose = composeViewState?.isInlineCompose;
        if (!isPopout(targetWindow)) {
            // If the compose is not in a popout, move the compose to a tab
            if (composeStore.primaryComposeId !== null) {
                const updatedTabId = await lazyMoveComposeToTab.importAndExecute(
                    composeViewState,
                    false /* isShown */,
                    false /* makeActive */
                );
                activeComposeTab = getTabStore().tabs.find(
                    tab => tab.id === updatedTabId
                ) as MailComposeTabViewState;
            }
            if (isInlineCompose) {
                await lazyMoveComposeToTab.importAndExecute(
                    composeViewState,
                    true /* isShown */,
                    true /* makeActive */
                );
                setIsOnSendEventTriggered(composeViewState.addin, false);
                await lazyTrySendMessageFromOnSendItemSwitchDialog.importAndExecute(
                    composeViewState,
                    targetWindow
                );
            } else {
                lazyDeinitializeAddinsForItem.import().then(deinitializeAddinsForItem => {
                    deinitializeAddinsForItem(hostItemIndex, window, composeViewState?.mailboxInfo);
                });
                activateTab(activeComposeTab);
            }

            // Launch the dialog only if the compose is not inline
            !isInlineCompose &&
                lazyOnSendAddinItemSwitch
                    .importAndExecute(composeViewState.mailboxInfo)
                    .then(async (response: DialogResponse) => {
                        switch (response) {
                            case DialogResponse.ok:
                                setIsOnSendEventTriggered(composeViewState.addin, false);
                                const popoutComposeAndWaitForProjection =
                                    await lazyPopoutComposeAndWaitForProjection.import();
                                try {
                                    const tabStore = getTabStore();
                                    for (const tab of tabStore.tabs) {
                                        if (
                                            tab.type === TabType.SecondaryReadingPane &&
                                            composeViewState.conversationId ===
                                                (
                                                    (tab as SecondaryReadingPaneTabViewState)
                                                        .data as SecondaryReadingPaneTabData
                                                )?.id?.Id
                                        ) {
                                            closeTab(tab);
                                        }
                                    }
                                    await popoutComposeAndWaitForProjection(
                                        composeViewState,
                                        targetWindow
                                    );
                                } catch (error) {
                                    trace.warn(error.message, 'addins');
                                }
                                targetWindow =
                                    (activeComposeTab &&
                                        getProjection(activeComposeTab?.id)?.window) ||
                                    window;

                                Promise.all([
                                    lazyCreateMailComposeAdapter.import(),
                                    lazyInitializeAddinsForItem.import(),
                                ]).then(([createMailComposeAdapter, initializeAddinsForItem]) => {
                                    const adapter = createMailComposeAdapter(
                                        composeViewState,
                                        targetWindow ?? window
                                    );

                                    initializeAddinsForItem(
                                        hostItemIndex,
                                        adapter,
                                        true /* renderTaskPaneInPanel */,
                                        targetWindow,
                                        composeViewState.mailboxInfo,
                                        composeViewState?.itemId?.Id
                                    );
                                });
                                await lazyTrySendMessageFromOnSendItemSwitchDialog.importAndExecute(
                                    composeViewState,
                                    targetWindow ?? window
                                );
                                break;
                            case DialogResponse.continue:
                                targetWindow =
                                    (activeComposeTab &&
                                        getProjection(activeComposeTab?.id)?.window) ||
                                    window;

                                // When send anyway is clicked, we should not trigger onSend event again
                                setIsOnSendEventTriggered(composeViewState.addin, true);
                                await lazyTrySendMessageFromOnSendItemSwitchDialog.importAndExecute(
                                    composeViewState,
                                    targetWindow ?? window
                                );
                                break;
                            case DialogResponse.cancel:
                                await lazyTrySaveAndCloseCompose.importAndExecute(composeViewState);
                                break;
                            case DialogResponse.dismiss:
                                // Re-initialize the add-ins for the item if the dialog is dismissed and the item is not sent
                                Promise.all([
                                    lazyCreateMailComposeAdapter.import(),
                                    lazyInitializeAddinsForItem.import(),
                                ]).then(([createMailComposeAdapter, initializeAddinsForItem]) => {
                                    const adapter = createMailComposeAdapter(
                                        composeViewState,
                                        targetWindow ?? window
                                    );

                                    initializeAddinsForItem(
                                        hostItemIndex,
                                        adapter,
                                        true /* renderTaskPaneInPanel */,
                                        targetWindow,
                                        composeViewState.mailboxInfo,
                                        composeViewState?.itemId?.Id
                                    );
                                });
                                setIsOnSendEventTriggered(composeViewState.addin, false);
                                break;
                            default:
                                // Handle any other cases or do nothing
                                break;
                        }
                    });
        }
    }
}
